import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FaHandshakeSimple } from 'react-icons/fa6';
import SmallLogo from '../../resources/images/small-logo-gray.svg';
import { oauthAuthorize } from '../../../app/auth/auth.actions';
import { useApiRequestState } from '../../../utils/api.utils';
import { isAllowedRedirectUri } from '../../../utils/isAllowedRedirectUri';
import { resolveOAuthClient } from '../../../utils/knownOAuthClients';

const appendOauthParams = (redirectUri: string, params: Record<string, string>) => {
  const url = new URL(redirectUri);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
};

export const OAuthConsentPage = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const [isAuthorizing] = useApiRequestState('oauthAuthorize');
  const isBusy = Boolean(isAuthorizing);
  const [allowBlockedError, setAllowBlockedError] = useState(false);

  const query = useMemo(() => new URLSearchParams(search), [search]);
  const state = query.get('state');
  const redirectUri = query.get('redirect_uri');
  const scope = query.get('scope') || undefined;
  const mcpCallback = query.get('mcp_callback');

  const allowedMcpCallbacks = useMemo(
    () =>
      (process.env.REACT_APP_MCP_OAUTH_CALLBACK_URL || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [],
  );

  useEffect(() => {
    setAllowBlockedError(false);
  }, [search]);

  const redirectWithParams = useCallback(
    (params: Record<string, string>) => {
      if (!redirectUri) {
        return;
      }
      window.location.replace(appendOauthParams(redirectUri, params));
    },
    [redirectUri],
  );

  const onAllow = useCallback(() => {
    if (!state || !redirectUri || !mcpCallback) {
      return;
    }

    if (!isAllowedRedirectUri(mcpCallback, allowedMcpCallbacks)) {
      // eslint-disable-next-line no-console
      console.error('OAuth consent: rejected mcp_callback not in allowlist');
      setAllowBlockedError(true);
      return;
    }

    dispatch(
      oauthAuthorize({
        redirectUri,
        scope,
        onSuccess: (code) => {
          window.location.replace(appendOauthParams(mcpCallback, { code, state }));
        },
      }),
    );
  }, [allowedMcpCallbacks, dispatch, mcpCallback, redirectUri, scope, state]);

  const onDeny = useCallback(() => {
    if (!state || !redirectUri) {
      return;
    }
    redirectWithParams({
      error: 'access_denied',
      error_description: 'The user denied the request',
      state,
    });
  }, [redirectUri, redirectWithParams, state]);

  const missingParams = !state || !redirectUri || !mcpCallback;
  const invalidMcpCallback = !!mcpCallback && !isAllowedRedirectUri(mcpCallback, allowedMcpCallbacks);
  const hasValidRequest = !missingParams && !invalidMcpCallback && !allowBlockedError;
  const knownClient = resolveOAuthClient(redirectUri);
  const requesterName = knownClient?.name || 'An app';

  return (
    <main id="app-root" className="login">
      <div className="fixed-panel">
        <div className="panel w500">
          <div className="panel-body px-5 pb-5">
            <div className="my-5 text-center">
              {knownClient ? (
                <div className="flex aic jcc" style={{ gap: '1rem' }}>
                  <img src={knownClient.logo} alt={knownClient.name} style={{ width: 56, height: 56, flexShrink: 0 }} />
                  <FaHandshakeSimple size={40} className="darkGray" aria-hidden />
                  <img src={SmallLogo} alt="account-able" style={{ height: 56, width: 'auto', flexShrink: 0 }} />
                </div>
              ) : (
                <img src={SmallLogo} alt="" className="img-fluid" />
              )}
            </div>
            <h1 style={{ fontWeight: 600, textAlign: 'center' }}>Authorize {requesterName}</h1>
            <h2 className="mt-4 text-center">
              <b>{requesterName}</b> wants access to your account-able account
            </h2>
            <ul className="mt-4 mb-5" style={{ listStyleType: 'disc', paddingLeft: '1.5rem' }}>
              <li>Read cards, action items, goals, meetings, personal notes, workspaces and personal information.</li>
            </ul>
            {!hasValidRequest && (invalidMcpCallback || allowBlockedError) && (
              <label style={{ color: 'red' }}>
                This connection request uses an unrecognized callback. Close this page and start again from your
                approved AI tool. No authorization was sent.
              </label>
            )}
            {!hasValidRequest && !invalidMcpCallback && !allowBlockedError && (
              <label style={{ color: 'red' }}>
                Invalid OAuth request. Please start the connection again from the app you were using.
              </label>
            )}
            <div className="panel-footer no-border flex jcr">
              <a
                className="link blueGray mr-2 close"
                onClick={onDeny}
                style={{
                  opacity: !state || !redirectUri || isBusy ? 0.5 : 1,
                  pointerEvents: !state || !redirectUri || isBusy ? 'none' : 'auto',
                }}
              >
                Deny
              </a>
              <button className="btn btn-lg green-bg wide-btn" onClick={onAllow} disabled={!hasValidRequest || isBusy}>
                Allow
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};
